import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { HtmlRenderer } from './HtmlRenderer';
import { UniversalDashboardRenderer } from '../../../../dashboard/components/common/UniversalDashboardRenderer';
import type { SimpleDashboardConfig } from '../../../../dashboard/components/Dashboard/Dashboard';
import {
  langFromPath,
  langColor,
  CopyButton,
  Spinner,
  TrafficLights,
  CollapseButton,
  CodePane,
} from './codeBlockUtils';
import { safeJsonParse } from '../../../../utils/jsonRepairUtils';
import { MarkdownWrapper } from './markdown/MarkdownWrapper';

import { useTranslation } from 'react-i18next';

interface FileEntry {
  filePath: string; // normalised — always populated (see normaliseFiles below)
  content?: string;
}

/** Accept both `path` (new) and `filePath` (legacy) on the raw tool input. */
function normaliseFiles(
  raw: Array<{ path?: string; filePath?: string; content?: string }> | undefined,
): FileEntry[] {
  return (raw ?? [])
    .map((f) => ({ filePath: f.path ?? f.filePath ?? '', content: f.content }))
    .filter((f) => f.filePath.length > 0);
}

const isPleriHtml = (p: string) =>
  p.endsWith('.html') || p.endsWith('.htm');
const isPleriJson = (p: string) => p.endsWith('.pleri.json');
const isMarkdown = (p: string) => p.endsWith('.md') || p.endsWith('.markdown');
const isRenderable = (p: string) =>
  isPleriHtml(p) || isPleriJson(p) || isMarkdown(p);

/**
 * Observes size changes on a block element and scrolls the outer chat container
 * to the bottom when it grows — but only if the user is already near the bottom.
 * Handles cases CSS overflow-anchor can't: iframe height changes via postMessage,
 * debounced markdown re-renders, dashboard loads.
 */
function useAutoScrollOnResize(
  ref: React.RefObject<HTMLDivElement | null>,
  isStreaming: boolean,
) {
  const conversationId = useChatStore((s) => s.activeConversationId);

  const scrollIfNearBottom = useCallback(() => {
    const el = document.getElementById(
      `chat-scrollable-container-${conversationId}`,
    );
    if (!el) {
      return;
    }
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 150) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
    }
  }, [conversationId]);

  useEffect(() => {
    if (!isStreaming || !ref.current) {
      return;
    }
    const observer = new ResizeObserver(scrollIfNearBottom);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isStreaming, ref, scrollIfNearBottom]);
}

function getDisplayTitle(filePath: string): string {
  return (filePath.split('/').pop() ?? filePath)
    .replace(/\.pleri\.json$/, '')
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]/g, ' ');
}

function basename(p: string) {
  return p.split('/').pop() ?? p;
}
function dirname(p: string) {
  return p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '';
}

// ─── Shared tab bar components ─────────────────────────────────────────────

const FileTab: React.FC<{
  filePath: string;
  active: boolean;
  onClick: () => void;
}> = ({ filePath, active, onClick }) => {
  const lang = langFromPath(filePath);
  const color = langColor(lang);
  const name = basename(filePath);
  const dir = dirname(filePath);
  return (
    <button
      onClick={onClick}
      title={filePath}
      className={`group relative flex max-w-[160px] min-w-0 flex-shrink-0 items-center gap-1.5 border-r border-gray-800/60 px-3 py-2 font-mono text-xs transition-colors duration-100 ${
        active
          ? 'bg-gray-950 text-gray-200'
          : 'bg-gray-900/30 text-gray-500 hover:bg-gray-900/60 hover:text-gray-300'
      }`}
    >
      {active && (
        <span
          className="absolute inset-x-0 top-0 h-[2px] rounded-b-sm"
          style={{ background: color }}
        />
      )}
      <span
        className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
        style={{ background: color }}
      />
      <span className="truncate">
        {dir && (
          <span className={active ? 'text-gray-500' : 'text-gray-600'}>
            {dir}/
          </span>
        )}
        {name}
      </span>
    </button>
  );
};

const CodeFooter: React.FC<{
  lang: string;
  lineCount: number;
  code: string;
}> = ({ lang, lineCount, code }) => {
  const { t } = useTranslation('pleri/chat');

  return (
    <div className="flex items-center justify-between border-t border-gray-800/60 bg-gray-900/40 px-3 py-1">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span className="tracking-wider uppercase">{lang}</span>
        <span>·</span>
        <span>
          {lineCount} {lineCount === 1 ? t('line') : t('lines')}
        </span>
      </div>
      <CopyButton text={code} />
    </div>
  );
};

const FileChip: React.FC<{ filePath: string }> = ({ filePath }) => {
  const lang = langFromPath(filePath);
  const color = langColor(lang);
  const name = basename(filePath);
  const dir = dirname(filePath);
  return (
    <div className="flex items-center gap-2 py-0.5 font-mono text-xs">
      <span
        className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
        style={{ background: color }}
      />
      <span>
        {dir && <span className="text-gray-600">{dir}/</span>}
        <span className="text-gray-300">{name}</span>
      </span>
    </div>
  );
};

// ─── Code tab body (tabs + code area, no header/collapse of its own) ───────

const CodeTabBody: React.FC<{
  codeFiles: FileEntry[];
  chipFiles: FileEntry[];
  isStreaming: boolean;
}> = ({ codeFiles, chipFiles, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const [userTab, setUserTab] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isStreaming && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  });

  const lastIdx = Math.max(0, codeFiles.length - 1);
  const safeTab = isStreaming ? lastIdx : Math.min(userTab ?? 0, lastIdx);
  const active = codeFiles[safeTab];
  const activeLang = active ? langFromPath(active.filePath) : 'text';
  const activeCode = active?.content ?? '';
  const activeLines = activeCode.split('\n').length;

  return (
    <>
      {codeFiles.length > 0 && (
        <div className="scrollbar-none flex overflow-x-auto border-b border-gray-800 bg-gray-900/50">
          {codeFiles.map((f, i) => (
            <FileTab
              key={f.filePath}
              filePath={f.filePath}
              active={safeTab === i}
              onClick={() => setUserTab(i)}
            />
          ))}
          <div className="flex-1 bg-gray-900/30" />
        </div>
      )}
      {active && (
        <div
          ref={scrollRef}
          style={{ height: '18rem' }}
          className="overflow-auto"
        >
          <CodePane code={activeCode} lang={activeLang} maxHeight="none" />
        </div>
      )}
      {chipFiles.length > 0 && (
        <div
          className={`px-4 py-2.5 ${active ? 'border-t border-gray-800/60' : ''}`}
        >
          <p className="mb-1.5 text-xs tracking-wider text-gray-600 uppercase">
            {t('alsoWritten')}
          </p>
          <div className="flex flex-col gap-0.5">
            {chipFiles.map((f) => (
              <FileChip key={f.filePath} filePath={f.filePath} />
            ))}
          </div>
        </div>
      )}
      {active && (
        <CodeFooter
          lang={activeLang}
          lineCount={activeLines}
          code={activeCode}
        />
      )}
    </>
  );
};

// ─── Collapsible code sub-panel (used in mixed mode) ──────────────────────

const CodeSubPanel: React.FC<{
  codeFiles: FileEntry[];
  chipFiles: FileEntry[];
  isStreaming: boolean;
}> = ({ codeFiles, chipFiles, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const [collapsed, setCollapsed] = useState(true);
  const count = codeFiles.length + chipFiles.length;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-800/70 bg-gray-950">
      <div className="flex items-center gap-2 border-b border-gray-800/70 bg-gray-900/60 px-3 py-1.5">
        <svg
          className="h-3 w-3 flex-shrink-0 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
          />
        </svg>
        <span className="flex-1 text-xs text-gray-500">
          {t('sourceFiles', { count })}
        </span>
        <CollapseButton
          collapsed={collapsed}
          onClick={() => setCollapsed((v) => !v)}
        />
      </div>
      {!collapsed && (
        <CodeTabBody
          codeFiles={codeFiles}
          chipFiles={chipFiles}
          isStreaming={isStreaming}
        />
      )}
    </div>
  );
};

// ─── Renderable file cards ─────────────────────────────────────────────────

const HtmlFileBlock: React.FC<{ file: FileEntry; isStreaming: boolean }> = ({
  file,
  isStreaming,
}) => {
  const { t } = useTranslation('pleri/chat');
  const title = getDisplayTitle(file.filePath);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-800/70">
      <div className="flex items-center gap-2 border-b border-gray-800/70 bg-gray-900/60 px-3 py-1.5">
        <svg
          className="h-3 w-3 flex-shrink-0 text-orange-400/70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
          />
        </svg>
        <span className="flex-1 truncate font-mono text-xs text-gray-300">
          {title || basename(file.filePath)}
        </span>
        <span className="text-xs tracking-wider text-orange-400/60 uppercase">
          {t('htmlPreview')}
        </span>
      </div>
      <HtmlRenderer
        html={file.content}
        title={title}
        isStreaming={isStreaming}
      />
    </div>
  );
};

const MarkdownFileBlock: React.FC<{
  file: FileEntry;
  isStreaming: boolean;
}> = ({ file, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const title = getDisplayTitle(file.filePath);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-800/70">
      <div className="flex items-center gap-2 border-b border-gray-800/70 bg-gray-900/60 px-3 py-1.5">
        <svg
          className="h-3 w-3 flex-shrink-0 text-gray-400/70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
        <span className="flex-1 truncate font-mono text-xs text-gray-300">
          {title || basename(file.filePath)}
        </span>
        <span className="text-xs tracking-wider text-gray-500 uppercase">
          {t('markdown')}
        </span>
        {isStreaming && !file.content && <Spinner />}
      </div>
      {/* Keep MarkdownWrapper mounted once content arrives — don't unmount on re-renders */}
      {file.content != null && (
        <MarkdownWrapper
          content={file.content}
          title={title}
          minimizedHeight="350px"
          enableScreenshot
          enablePDF
        />
      )}
    </div>
  );
};

const JsonFileBlock: React.FC<{ file: FileEntry; isStreaming: boolean }> = ({
  file,
  isStreaming,
}) => {
  const { t } = useTranslation('pleri/chat');
  const title = getDisplayTitle(file.filePath);
  if (!file.content) {
    return null;
  }
  const config = safeJsonParse<SimpleDashboardConfig>(
    file.content,
    'pleri json dashboard',
  );
  if (!config) {
    return null;
  }
  return (
    <div className="overflow-hidden rounded-lg border border-gray-800/70">
      <div className="flex items-center gap-2 border-b border-gray-800/70 bg-gray-900/60 px-3 py-1.5">
        <svg
          className="h-3 w-3 flex-shrink-0 text-purple-400/70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
          />
        </svg>
        <span className="flex-1 truncate font-mono text-xs text-gray-300">
          {title || basename(file.filePath)}
        </span>
        <span className="text-xs tracking-wider text-purple-400/60 uppercase">
          {t('dashboard')}
        </span>
      </div>
      <UniversalDashboardRenderer
        config={config}
        variant="expandable"
        enableExpand
        enablePDF
        enableScreenshot
        enableScaling
        title={title}
        height="350px"
        className="my-1"
        designWidth={1400}
        maxScale={0.5}
        minScale={0.25}
        isStreaming={isStreaming}
      />
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────

export const WriteFilesBlock: React.FC<{
  content: ToolUseBlock;
  isStreaming: boolean;
}> = ({ content, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const files = normaliseFiles(
    content.input?.files as Array<{ path?: string; filePath?: string; content?: string }> | undefined,
  );

  if (!files?.length) {
    if (!isStreaming) {
      return null;
    }
    return (
      <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs shadow-lg">
        <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
          <TrafficLights />
          <span className="flex-1 text-xs text-gray-400">
            {t('writingFiles')}
          </span>
          <Spinner />
        </div>
      </div>
    );
  }

  const codeFiles = files.filter(
    (f) => !isRenderable(f.filePath) && f.content != null,
  );
  const chipFiles = files.filter(
    (f) => !isRenderable(f.filePath) && f.content == null,
  );
  const renderableFiles = files.filter((f) => isRenderable(f.filePath));
  const hasRenderables = renderableFiles.length > 0;
  const totalCount = files.length;

  // ── Code-only: original behaviour (auto-close, full-width panel) ─────────
  if (!hasRenderables) {
    return (
      <CodeOnlyWrapper
        codeFiles={codeFiles}
        chipFiles={chipFiles}
        totalCount={totalCount}
        isStreaming={isStreaming}
      />
    );
  }

  // ── Mixed: no auto-close, code tucked away, previews always visible ──────
  return (
    <MixedLayout
      codeFiles={codeFiles}
      chipFiles={chipFiles}
      renderableFiles={renderableFiles}
      totalCount={totalCount}
      isStreaming={isStreaming}
    />
  );
};

const MixedLayout: React.FC<{
  codeFiles: FileEntry[];
  chipFiles: FileEntry[];
  renderableFiles: FileEntry[];
  totalCount: number;
  isStreaming: boolean;
}> = ({ codeFiles, chipFiles, renderableFiles, totalCount, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const blockRef = useRef<HTMLDivElement>(null);
  useAutoScrollOnResize(blockRef, isStreaming);
  const hasCodeOrChips = codeFiles.length > 0 || chipFiles.length > 0;

  return (
    <div
      ref={blockRef}
      className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs shadow-lg"
    >
      <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <TrafficLights />
        <svg
          className="h-3.5 w-3.5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
          />
        </svg>
        <span className="flex-1 text-xs text-gray-400">
          <span>{isStreaming ? t('writing') : t('wrote')}</span>{' '}
          <span className="text-gray-200">
            {t('fileCount', { count: totalCount })}
          </span>
        </span>
        <span
          className={`transition-opacity duration-300 ${isStreaming ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <Spinner />
        </span>
      </div>
      <div className="flex flex-col gap-2 p-2">
        {hasCodeOrChips && (
          <CodeSubPanel
            codeFiles={codeFiles}
            chipFiles={chipFiles}
            isStreaming={isStreaming}
          />
        )}
        {renderableFiles.map((f) =>
          isPleriHtml(f.filePath) ? (
            <HtmlFileBlock
              key={f.filePath}
              file={f}
              isStreaming={isStreaming}
            />
          ) : isMarkdown(f.filePath) ? (
            <MarkdownFileBlock
              key={f.filePath}
              file={f}
              isStreaming={isStreaming}
            />
          ) : (
            <JsonFileBlock
              key={f.filePath}
              file={f}
              isStreaming={isStreaming}
            />
          ),
        )}
      </div>
    </div>
  );
};

// ─── Code-only wrapper ─────────────────────────────────────────────────────

const CodeOnlyWrapper: React.FC<{
  codeFiles: FileEntry[];
  chipFiles: FileEntry[];
  totalCount: number;
  isStreaming: boolean;
}> = ({ codeFiles, chipFiles, totalCount, isStreaming }) => {
  const { t } = useTranslation('pleri/chat');
  const [collapsed, setCollapsed] = useState(true);
  const userInteracted = useRef(false);
  const blockRef = useRef<HTMLDivElement>(null);
  useAutoScrollOnResize(blockRef, isStreaming);

  useEffect(() => {
    if (userInteracted.current) {
      return;
    }
    if (isStreaming) {
      setCollapsed(false);
    } else {
      const t = setTimeout(() => setCollapsed(true), 700);
      return () => clearTimeout(t);
    }
  }, [isStreaming]);

  return (
    <div
      ref={blockRef}
      className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs shadow-lg"
    >
      <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <TrafficLights />
        <svg
          className="h-3.5 w-3.5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
          />
        </svg>
        <span className="flex-1 text-xs text-gray-400">
          <span className="transition-all duration-300">
            {isStreaming ? t('writing') : t('wrote')}
          </span>{' '}
          <span className="text-gray-200">
            {t('fileCount', { count: totalCount })}
          </span>
        </span>
        <span
          className={`transition-opacity duration-300 ${isStreaming ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <Spinner />
        </span>
        <CollapseButton
          collapsed={collapsed}
          onClick={() => {
            userInteracted.current = true;
            setCollapsed((v) => !v);
          }}
        />
      </div>
      {!collapsed && (
        <CodeTabBody
          codeFiles={codeFiles}
          chipFiles={chipFiles}
          isStreaming={isStreaming}
        />
      )}
    </div>
  );
};
